// Curated skills vocabulary for the autosuggest input.
// Canonical display names + alias map (so "springboot" -> "Spring Boot").
// A research-backed / larger taxonomy can replace this later (P2); the shape
// (SKILLS, ALIASES, suggestSkills, canonicalizeSkill, POPULAR) stays stable.

export const SKILLS = [
  // Languages
  "JavaScript", "TypeScript", "Python", "Java", "Go", "C++", "C#", "Ruby", "PHP",
  "Kotlin", "Swift", "Rust", "Scala", "SQL", "Bash", "R", "MATLAB",
  // Backend / web
  "Node.js", "Express", "Spring Boot", "Django", "Flask", "FastAPI", ".NET",
  "Ruby on Rails", "GraphQL", "REST APIs", "gRPC", "Microservices", "WebSockets",
  // Frontend
  "React", "Next.js", "Angular", "Vue.js", "Redux", "HTML", "CSS", "Sass",
  "Tailwind CSS", "Webpack", "Vite", "Material UI", "Accessibility",
  // Mobile
  "Android", "iOS", "Flutter", "React Native", "Jetpack Compose", "SwiftUI",
  // Data / ML
  "Pandas", "NumPy", "scikit-learn", "PyTorch", "TensorFlow", "Keras",
  "Machine Learning", "Deep Learning", "NLP", "Computer Vision", "LLMs",
  "Hugging Face", "Spark", "Hadoop", "Airflow", "dbt", "Snowflake", "BigQuery",
  "Tableau", "Power BI", "Looker", "Excel", "Statistics", "A/B Testing",
  "Data Modeling", "ETL", "Data Warehousing",
  // Databases
  "PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch", "Cassandra",
  "DynamoDB", "SQLite", "Oracle",
  // DevOps / cloud
  "AWS", "Azure", "GCP", "Docker", "Kubernetes", "Terraform", "Ansible",
  "Jenkins", "GitHub Actions", "CI/CD", "Linux", "Prometheus", "Grafana",
  "Helm", "Nginx", "Kafka", "RabbitMQ",
  // Testing
  "Selenium", "Cypress", "Playwright", "Jest", "JUnit", "TestNG", "Postman",
  "Appium", "Test Automation",
  // Security
  "Penetration Testing", "OWASP", "SIEM", "IAM", "Network Security", "Cryptography",
  // Tools / practices
  "Git", "Jira", "Agile", "Scrum", "System Design", "Unit Testing", "OOP",
  "Data Structures", "Algorithms",
  // Product / design
  "Figma", "Adobe XD", "Sketch", "User Research", "Wireframing", "Prototyping",
  "Design Systems", "Roadmapping", "Stakeholder Management",
  // Marketing
  "SEO", "SEM", "Google Ads", "Google Analytics", "Content Marketing",
  "Email Marketing", "Social Media Marketing", "HubSpot", "Copywriting",
  "Performance Marketing", "Conversion Rate Optimization",
  // Sales / CRM
  "Salesforce", "Lead Generation", "Account Management", "Negotiation",
  "B2B Sales", "Cold Calling", "Pipeline Management",
  // Finance / ops / HR
  "Financial Modeling", "Budgeting", "Forecasting", "Accounting", "SAP", "Tally",
  "Supply Chain", "Project Management", "Process Improvement",
  "Talent Acquisition", "Onboarding", "Payroll",
  // Soft skills
  "Communication", "Leadership", "Problem Solving", "Collaboration",
];

// Lower-cased alias -> canonical name.
export const ALIASES = {
  "js": "JavaScript", "ts": "TypeScript", "node": "Node.js", "nodejs": "Node.js",
  "node.js": "Node.js", "reactjs": "React", "react.js": "React", "next": "Next.js",
  "nextjs": "Next.js", "vue": "Vue.js", "vuejs": "Vue.js", "springboot": "Spring Boot",
  "spring boot": "Spring Boot", "spring": "Spring Boot", "py": "Python",
  "golang": "Go", "postgres": "PostgreSQL", "postgresql": "PostgreSQL",
  "mongo": "MongoDB", "k8s": "Kubernetes", "tf": "Terraform", "gha": "GitHub Actions",
  "ci/cd": "CI/CD", "cicd": "CI/CD", "ml": "Machine Learning", "dl": "Deep Learning",
  "nlp": "NLP", "llm": "LLMs", "llms": "LLMs", "powerbi": "Power BI",
  "power bi": "Power BI", "sklearn": "scikit-learn", "tensorflow": "TensorFlow",
  "pytorch": "PyTorch", "rest": "REST APIs", "restful": "REST APIs",
  "graphql": "GraphQL", "css3": "CSS", "html5": "HTML", "tailwind": "Tailwind CSS",
  "rn": "React Native", "react native": "React Native", "gcp": "GCP", "aws": "AWS",
  "dsa": "Data Structures", "oops": "OOP", "seo": "SEO", "sem": "SEM",
  "cro": "Conversion Rate Optimization", "sfdc": "Salesforce",
};

const _LOWER_TO_CANON = (() => {
  const m = {};
  for (const s of SKILLS) m[s.toLowerCase()] = s;
  return m;
})();

// Generic popular quick-add skills shown when the input is empty and no role
// is known (e.g. landing widget before a role is picked).
export const POPULAR = [
  "JavaScript", "Python", "SQL", "React", "Node.js", "AWS", "Docker", "Git",
];

// Role-aware quick-add lists. When a target role is selected, the empty-input
// suggestions should match that role's field — a Sales role shouldn't be told
// to add Node.js. Keyed by a coarse category we infer from the role title.
export const SKILLS_BY_CATEGORY = {
  sales:     ["Salesforce", "Lead Generation", "B2B Sales", "Negotiation", "Account Management", "Cold Calling", "Pipeline Management", "Communication"],
  marketing: ["SEO", "Google Ads", "Content Marketing", "Social Media Marketing", "Google Analytics", "Performance Marketing", "Email Marketing", "Copywriting"],
  data:      ["SQL", "Python", "Pandas", "Machine Learning", "Tableau", "Power BI", "Statistics", "Excel"],
  frontend:  ["JavaScript", "React", "TypeScript", "HTML", "CSS", "Next.js", "Tailwind CSS", "Redux"],
  backend:   ["Java", "Python", "Node.js", "SQL", "REST APIs", "Microservices", "Spring Boot", "Docker"],
  mobile:    ["Kotlin", "Swift", "Flutter", "React Native", "Android", "iOS", "Java", "REST APIs"],
  devops:    ["AWS", "Docker", "Kubernetes", "Terraform", "CI/CD", "Linux", "Jenkins", "Ansible"],
  design:    ["Figma", "User Research", "Wireframing", "Prototyping", "Design Systems", "Adobe XD", "Sketch", "Accessibility"],
  product:   ["Roadmapping", "Stakeholder Management", "Agile", "User Research", "A/B Testing", "Jira", "Data Modeling", "Communication"],
  finance:   ["Financial Modeling", "Budgeting", "Forecasting", "Accounting", "Excel", "SAP", "Tally", "Statistics"],
  hr:        ["Talent Acquisition", "Onboarding", "Payroll", "Stakeholder Management", "Process Improvement", "Project Management", "Communication", "Leadership"],
  security:  ["Penetration Testing", "OWASP", "Network Security", "SIEM", "IAM", "Cryptography", "Linux", "Python"],
  qa:        ["Selenium", "Cypress", "Test Automation", "Jest", "Postman", "Playwright", "JUnit", "Appium"],
  general:   ["Communication", "Leadership", "Problem Solving", "Project Management", "Excel", "Collaboration", "Agile", "Stakeholder Management"],
};

// Map a free-text role title to a category. Order matters — more specific
// patterns first (e.g. "data engineer" -> data before the generic "engineer").
const _ROLE_PATTERNS = [
  ["security",  /\b(security|penetration|infosec|soc analyst|cyber)\b/],
  ["qa",        /\b(qa|quality assurance|test engineer|sdet|automation test|tester)\b/],
  ["devops",    /\b(devops|sre|site reliability|cloud engineer|infrastructure|platform engineer)\b/],
  ["data",      /\b(data analyst|data engineer|data scientist|analytics|machine learning|ml engineer|ai engineer|business intelligence|\bbi\b|\bdata\b)\b/],
  ["mobile",    /\b(mobile|android|ios|flutter)\b/],
  ["frontend",  /\b(frontend|front[\s-]?end|ui developer|ui engineer)\b/],
  ["design",    /\b(design|ux|graphic|illustrator|product design)\b/],
  ["product",   /\b(product manager|product owner|program manager)\b/],
  ["marketing", /\b(marketing|seo|sem|growth|content|brand|social media|digital market)\b/],
  ["sales",     /\b(sales|business develop|account executive|account manager|relationship manager|inside sales|territory)\b/],
  ["finance",   /\b(finance|financial|account(ant|ing)|audit|investment|treasury|banker)\b/],
  ["hr",        /\b(hr|human resource|recruit|talent|people ops|payroll)\b/],
  ["backend",   /\b(backend|back[\s-]?end|full[\s-]?stack|software|developer|engineer|programmer|sde|java|python|\.net)\b/],
];

export function categoryForRole(role) {
  const r = (role || "").toLowerCase();
  if (!r) return null;
  for (const [cat, re] of _ROLE_PATTERNS) {
    if (re.test(r)) return cat;
  }
  return "general";
}

// Popular quick-add skills for a role (falls back to the generic POPULAR list
// when no role/category match is found).
export function popularSkillsForRole(role) {
  const cat = categoryForRole(role);
  return (cat && SKILLS_BY_CATEGORY[cat]) || POPULAR;
}

// Acronyms that should render upper-cased when they come from the backend
// keyword taxonomy (which stores everything lower-cased for matching).
const _ACRONYMS = new Set([
  "seo", "sem", "ppc", "crm", "ats", "api", "sql", "aws", "gcp", "ui", "ux",
  "ci/cd", "kpis", "hris", "roi", "b2b", "p&l", "gaap", "sop", "iam", "siem",
  "owasp", "nlp", "llm", "iac", "soc", "mvvm", "etl", "bi", "qa",
]);

// Display-ready name for a raw (often lower-cased) skill/keyword. Prefers the
// canonical map, upper-cases known acronyms, else title-cases. Used for the
// role-keyword suggestions coming from the backend taxonomy.
export function prettySkill(raw) {
  const t = (raw || "").trim();
  if (!t) return "";
  const low = t.toLowerCase();
  if (ALIASES[low]) return ALIASES[low];
  if (_LOWER_TO_CANON[low]) return _LOWER_TO_CANON[low];
  if (_ACRONYMS.has(low)) return low.toUpperCase();
  return low.split(/\s+/).map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
}

// Resolve user input to a canonical skill name (alias-aware); else title-case it.
export function canonicalizeSkill(raw) {
  const t = (raw || "").trim();
  if (!t) return "";
  const low = t.toLowerCase();
  if (ALIASES[low]) return ALIASES[low];
  if (_LOWER_TO_CANON[low]) return _LOWER_TO_CANON[low];
  return t.replace(/\s+/g, " ");
}

// Up to `limit` suggestions for a query — prefix matches first, then substring.
// When the query is empty, show role-relevant popular skills if a `role` is
// provided (else the generic POPULAR list).
export function suggestSkills(query, exclude = [], limit = 6, role = "") {
  const q = (query || "").trim().toLowerCase();
  const taken = new Set(exclude.map((s) => s.toLowerCase()));
  if (!q) return popularSkillsForRole(role).filter((s) => !taken.has(s.toLowerCase())).slice(0, limit);
  const prefix = [], substr = [];
  for (const s of SKILLS) {
    const sl = s.toLowerCase();
    if (taken.has(sl)) continue;
    if (sl.startsWith(q)) prefix.push(s);
    else if (sl.includes(q)) substr.push(s);
  }
  // surface an alias hit too (e.g. "springboot")
  if (ALIASES[q] && !taken.has(ALIASES[q].toLowerCase())) prefix.unshift(ALIASES[q]);
  return [...new Set([...prefix, ...substr])].slice(0, limit);
}
